import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  User,
  Phone,
  Mail,
  Calendar as CalendarIcon,
  ScrollText,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandMark from '../components/BrandMark';
import SignaturePadField from '../components/SignaturePadField';
import { WAIVER_TEXT } from '../lib/waiverText';
import { saveWaiver, generateId } from '../lib/waiverStorage';

const todayISO = () => {
  const d = new Date();
  const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  return iso;
};

export default function Waiver() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    date: todayISO(),
  });
  const [errors, setErrors] = useState({});
  const [readAck, setReadAck] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const scrollBoxRef = useRef(null);
  const sigRef = useRef(null);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      errs.name = 'Please enter your full name.';
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 7) errs.phone = 'Please enter a valid phone number.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = 'Please enter a valid email address.';
    if (!form.date) errs.date = 'Please select a date.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNextFromInfo = () => {
    if (!validateStep1()) {
      toast.error('Please fix the fields highlighted below.');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const el = scrollBoxRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (nearBottom) setScrolledToBottom(true);
  };

  const goNextFromText = () => {
    if (!readAck) {
      toast.error('Please acknowledge that you have read the waiver.');
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please provide your signature.');
      return;
    }
    const signature = sigRef.current.toDataURL();
    const waiver = {
      id: generateId(),
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      signature,
      signedAt: new Date().toISOString(),
    };
    saveWaiver(waiver);
    setSubmitted(true);
    toast.success('Thank you! Your waiver has been signed.');
  };

  // Reset any submitted state if user navigates back into flow
  useEffect(() => {
    if (step === 1) {
      setSubmitted(false);
    }
  }, [step]);

  if (submitted) {
    return (
      <div className="min-h-screen frost-bg relative">
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-10">
          <div
            className="w-full max-w-2xl glass-card rounded-[2.5rem] p-10 sm:p-14 text-center reveal"
            data-testid="waiver-success-panel"
          >
            <div className="mx-auto w-24 h-24 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mb-8">
              <CheckCircle2 className="w-14 h-14 text-blue-500" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-slate-100 tracking-tight">
              You&rsquo;re all set.
            </h1>
            <p className="mt-4 text-lg text-slate-300">
              Thank you, <span className="font-semibold text-blue-300">{form.name.split(' ')[0]}</span>. Your waiver is signed and stored securely.
              Please hand the tablet back to our staff.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="pill-btn h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30"
                data-testid="waiver-success-home-button"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen frost-bg relative">
      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-4xl">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => (step === 1 ? navigate('/') : setStep(step - 1))}
              className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-slate-900/70 border border-blue-500/30 text-blue-300 font-semibold hover:bg-slate-800 active:scale-95"
              data-testid="waiver-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <BrandMark size="sm" showAddress={false} />
            <div className="w-[92px]" />
          </div>

          {/* Stepper */}
          <div className="mb-8 flex items-center justify-center gap-3">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step >= s
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-slate-900/80 border border-blue-500/30 text-blue-400'
                  }`}
                  data-testid={`waiver-step-indicator-${s}`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-[2px] ${step > s ? 'bg-blue-500' : 'bg-blue-500/30'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="glass-card rounded-[2rem] p-6 sm:p-10 reveal">
            {/* Step 1: Client Info */}
            {step === 1 && (
              <div data-testid="waiver-step-1">
                <h2 className="font-display text-3xl sm:text-4xl text-slate-100 tracking-tight">
                  Your Information
                </h2>
                <p className="mt-2 text-slate-300">
                  Please enter your details so we can associate this waiver with your visit.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 flex items-center gap-2">
                      <User className="w-3.5 h-3.5" /> Full Name
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="Jane Doe"
                      autoComplete="name"
                      className="glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100 placeholder:text-slate-400"
                      data-testid="waiver-name-input"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600" data-testid="waiver-name-error">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" /> Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="(305) 555 0134"
                      autoComplete="tel"
                      className="glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100 placeholder:text-slate-400"
                      data-testid="waiver-phone-input"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600" data-testid="waiver-phone-error">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      placeholder="jane@example.com"
                      autoComplete="email"
                      className="glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100 placeholder:text-slate-400"
                      data-testid="waiver-email-input"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600" data-testid="waiver-email-error">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 flex items-center gap-2">
                      <CalendarIcon className="w-3.5 h-3.5" /> Date
                    </label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setField('date', e.target.value)}
                      className="glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100"
                      data-testid="waiver-date-input"
                    />
                    {errors.date && (
                      <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                    )}
                  </div>
                </div>

                <div className="mt-10 flex justify-end">
                  <button
                    onClick={goNextFromInfo}
                    className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30"
                    data-testid="waiver-continue-to-text-button"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Waiver text */}
            {step === 2 && (
              <div data-testid="waiver-step-2">
                <div className="flex items-center gap-3">
                  <ScrollText className="w-6 h-6 text-blue-500" />
                  <h2 className="font-display text-3xl sm:text-4xl text-slate-100 tracking-tight">
                    Liability Waiver
                  </h2>
                </div>
                <p className="mt-2 text-slate-300">
                  Please read the following carefully. Scroll to the bottom to acknowledge.
                </p>

                <div
                  ref={scrollBoxRef}
                  onScroll={handleScroll}
                  className="waiver-scroll mt-6 h-[46vh] overflow-y-auto border-2 border-white/10 bg-slate-900/70 p-6 sm:p-8 rounded-3xl text-[15px] sm:text-base leading-relaxed text-slate-200 shadow-inner whitespace-pre-line"
                  data-testid="waiver-text-scroll"
                >
                  {WAIVER_TEXT}
                </div>

                {!scrolledToBottom && (
                  <p
                    className="mt-3 text-sm text-blue-400 italic"
                    data-testid="waiver-scroll-hint"
                  >
                    Scroll down to the end to enable acknowledgment.
                  </p>
                )}

                <label
                  className={`mt-6 flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                    scrolledToBottom
                      ? 'border-blue-400 bg-slate-900/70 hover:bg-slate-800'
                      : 'border-blue-500/20 bg-slate-900/30 cursor-not-allowed opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={readAck}
                    disabled={!scrolledToBottom}
                    onChange={(e) => setReadAck(e.target.checked)}
                    className="mt-1 w-6 h-6 accent-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    data-testid="waiver-acknowledge-checkbox"
                  />
                  <span className="text-slate-100 text-base sm:text-lg">
                    I have read, understand, and voluntarily agree to the terms
                    of this liability waiver.
                  </span>
                </label>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={goNextFromText}
                    disabled={!readAck}
                    className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    data-testid="waiver-continue-to-sign-button"
                  >
                    Continue to Signature
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Signature */}
            {step === 3 && (
              <div data-testid="waiver-step-3">
                <h2 className="font-display text-3xl sm:text-4xl text-slate-100 tracking-tight">
                  Sign to Confirm
                </h2>
                <p className="mt-2 text-slate-300">
                  Signing below indicates you agree to the terms of the waiver.
                </p>

                <div className="mt-8">
                  <SignaturePadField
                    ref={sigRef}
                    onEndStroke={(has) => setHasSignature(has)}
                  />
                </div>

                <div className="mt-8 p-5 rounded-2xl bg-blue-500/10/60 border border-blue-500/30">
                  <p className="text-sm text-blue-200">
                    <span className="font-bold">Signing as: </span>
                    {form.name} &middot; {form.email} &middot; {form.date}
                  </p>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={!hasSignature}
                    className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    data-testid="waiver-submit-button"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    Agree & Submit
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
